import { SpaceType } from 'src/components/SearchBar/types'
import { haversineKm } from 'src/constants/onDemandCoverage'
import {
  PillarPage,
  SEARCH_PAGE_CITY_MAP,
  SearchPageCity
} from 'src/constants/searchPages'

/**
 * When a city/hood page renders the directory as a server-rendered CITY GUIDE
 * instead of the muted, lazy tier floor below the results.
 *
 *  · `zero` (2026-08-28): the page has no SpotMe listing at all — any pillar
 *    with directory rows. Nothing to dilute.
 *  · `weak` (2026-10-20, Jules: "supply is so weak that we don't even benefit
 *    from that thin supply — promote the directory, just keep SpotMe listings
 *    on top"): a storage or parking page with only a handful of SpotMe
 *    listings. The guide renders right after them, still strictly below
 *    every SpotMe listing of any model.
 *
 * Measured on prod the day it shipped (storage city pages, zoom 11): CDMX 11
 * SpotMe listings vs 99 directory facilities, Guadalajara 6 vs 23, Querétaro
 * 2 vs 19, Puebla 1 vs 8 — and Monterrey 30 vs 25, the one market where our
 * own supply is real.
 */
export type DirectoryGuideMode = 'zero' | 'weak'

/**
 * A page with FEWER SpotMe results than this is "weak supply". Self-updating:
 * as a city's supply grows past it, the page goes back to the tier floor.
 */
export const DIRECTORY_GUIDE_WEAK_SUPPLY_MAX = 15

/**
 * Monterrey metro never gets the weak-supply guide: it is the Q4 market, our
 * supply there is real, and every unsigned operator there is a sales target
 * (sales/playbooks/lead-como-pitch.md). ~60 km from the city center covers
 * the metro (García, Juárez, Escobedo) without reaching Saltillo (~75 km).
 */
const WEAK_GUIDE_EXCLUDED_RADIUS_KM = 60

/**
 * The two pillars the directory holds rows for. Parking added the same day
 * (Jules, 2026-10-20) after the count: GDL 2 SpotMe parking listings vs 359
 * directory rows, León 2 vs 92, Puebla 0 vs 112 — while CDMX (24) stays above
 * the threshold. Published parking rows always carry a callable phone
 * (`isConfidentParking`), so "Ver teléfono" works on every card.
 */
const WEAK_GUIDE_PILLARS: readonly string[] = [
  PillarPage.SelfStorage,
  PillarPage.Parking
]

export function directoryGuideMode(args: {
  pillar: string
  searchTotal: number
  center: { lat: number; lng: number } | null
}): DirectoryGuideMode | null {
  const { pillar, searchTotal, center } = args
  if (searchTotal === 0) return 'zero'
  if (!WEAK_GUIDE_PILLARS.includes(pillar)) return null
  if (searchTotal >= DIRECTORY_GUIDE_WEAK_SUPPLY_MAX) return null
  // No resolved center means we cannot rule Monterrey out — stay on the floor.
  if (!center) return null
  const mty = SEARCH_PAGE_CITY_MAP[SearchPageCity.Monterrey]?.center
  if (!mty) return null
  const km = haversineKm(center.lat, center.lng, mty.lat, mty.lng)
  return km <= WEAK_GUIDE_EXCLUDED_RADIUS_KM ? null : 'weak'
}

/**
 * The pillar a search speaks for, so `/search` (no pillar in the URL) can use
 * the same rule as the city pages: the route's own pillar when there is one,
 * else a search filtered to exactly ONE directory category. Anything else —
 * "all", mixed or B2B types — has no single directory answer, and returns ''
 * so only the zero-results mode can apply.
 */
export function guidePillarFor(
  routePillar: string | string[] | undefined,
  spaceTypes: SpaceType[] | undefined
): string {
  const pillar = Array.isArray(routePillar) ? routePillar[0] : routePillar
  if (pillar) return pillar
  if (spaceTypes?.length !== 1) return ''
  if (spaceTypes[0] === SpaceType.SELF_STORAGE) return PillarPage.SelfStorage
  if (spaceTypes[0] === SpaceType.PARKING) return PillarPage.Parking
  return ''
}
